import { computed } from 'vue'
import { resolveFontAssets, CJK_FONT_FAMILY_STACK } from '~/utils/fontAssets'
import type { SupportedLocale } from '~/utils/localeController'

export function useCmsFontFace(familyKeyRef: () => string, localeRef: () => SupportedLocale) {
  const fontAssets = computed(() => {
    const familyKey = familyKeyRef()
    const locale = localeRef()
    return resolveFontAssets(familyKey, locale)
  })

  useHead(() => {
    const assets = fontAssets.value
    return {
      link: assets.preload.map((href) => ({
        rel: 'preload',
        as: 'font',
        type: 'font/woff2',
        href,
        crossorigin: 'anonymous',
      })),
    }
  })

  // Return the computed full fontFamily CSS value including fallback stack
  const resolvedFamily = computed(() => {
    const familyKey = familyKeyRef()
    const locale = localeRef()
    
    // CJK languages should use CJK system font stack first
    if (['zh', 'ja', 'ko'].includes(locale)) {
      return CJK_FONT_FAMILY_STACK
    }

    // Otherwise, prepend the custom Google Font family name and fallback
    const normalizedKey = String(familyKey || '').trim().toLowerCase()
    
    // Convert key to neat display name
    const familyDisplayNames: Record<string, string> = {
      'roboto': 'Roboto',
      'open-sans': 'Open Sans',
      'lato': 'Lato',
      'montserrat': 'Montserrat',
      'poppins': 'Poppins',
      'nunito-sans': 'Nunito Sans',
      'inter': 'Inter',
      'jetbrains-mono': 'JetBrains Mono',
    }
    
    const displayName = familyDisplayNames[normalizedKey] || 'Roboto'
    return `'${displayName}', ${CJK_FONT_FAMILY_STACK}`
  })

  return {
    resolvedFamily,
  }
}
